import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Wallet, BookOpen, Users as UsersIcon, ShieldCheck, Settings,
  ChevronsUpDown, Check, LogOut, Sparkles, FileText, Truck, Fuel,
  Wrench, ShoppingCart, Package, ClipboardList, Hammer, Building2,
  AlertTriangle, HardHat, Scale, Mail as MailIcon, GitBranch, Bot, Database,
  Receipt, UserCircle, Scan,
} from "lucide-react";
import { useEntity } from "@/lib/entity";
import { useAuth } from "@/lib/auth";

// Sections organisées
const SECTIONS = [
  {
    label: "Pilotage",
    items: [
      { to: "/app/dashboard", label: "Tableau de bord", icon: LayoutDashboard },
      { to: "/app/ai-agents", label: "Agents IA", icon: Bot, badge: "PRO" },
      { to: "/app/ocr", label: "OCR Facture", icon: Scan, badge: "IA" },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/app/treasury", label: "Trésorerie", icon: Wallet },
      { to: "/app/accounting", label: "Comptabilité", icon: BookOpen },
      { to: "/app/invoices", label: "Facturation", icon: Receipt },
    ],
  },
  {
    label: "Opérations",
    items: [
      { to: "/app/suppliers", label: "Fournisseurs", icon: Building2 },
      { to: "/app/clients", label: "Clients", icon: UserCircle },
      { to: "/app/purchases", label: "Achats (BC)", icon: ShoppingCart },
      { to: "/app/stock", label: "Stock", icon: Package },
    ],
  },
  {
    label: "Flotte",
    items: [
      { to: "/app/vehicles", label: "Parc auto", icon: Truck },
      { to: "/app/maintenance", label: "Maintenance", icon: Wrench },
      { to: "/app/fuel", label: "Carburant", icon: Fuel },
    ],
  },
  {
    label: "Métiers",
    items: [
      { to: "/app/pos", label: "Caisse / POS", icon: ClipboardList },
      { to: "/app/chantiers", label: "Chantiers", icon: Hammer },
    ],
  },
  {
    label: "Ressources Humaines",
    items: [
      { to: "/app/hr", label: "Personnel", icon: UsersIcon },
      { to: "/app/epi", label: "Dotations EPI", icon: HardHat },
    ],
  },
  {
    label: "Gouvernance",
    items: [
      { to: "/app/hse", label: "HSE", icon: AlertTriangle },
      { to: "/app/legal", label: "Juridique", icon: Scale },
      { to: "/app/mail", label: "Courrier", icon: MailIcon },
      { to: "/app/workflows", label: "Workflows", icon: GitBranch },
    ],
  },
  {
    label: "Administration",
    roles: ["PDG", "DG"],
    items: [
      { to: "/app/users", label: "Utilisateurs", icon: UsersIcon, roles: ["PDG", "DG"] },
      { to: "/app/audit", label: "Audit", icon: ShieldCheck, roles: ["PDG", "DG"] },
      { to: "/app/backup", label: "Sauvegardes", icon: Database, roles: ["PDG"] },
      { to: "/app/settings", label: "Paramètres", icon: Settings },
    ],
  },
];

export default function Sidebar({ onOpenAI }) {
  const { entities, currentEntity, selectEntity } = useEntity();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [entityOpen, setEntityOpen] = useState(false);
  const entityRef = React.useRef(null);

  React.useEffect(() => {
    if (!entityOpen) return;
    const onClick = (e) => { if (entityRef.current && !entityRef.current.contains(e.target)) setEntityOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setEntityOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, [entityOpen]);

  const canSeeSection = (s) => !s.roles || s.roles.includes(user?.role);
  const canSeeItem = (i) => !i.roles || i.roles.includes(user?.role);

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-sidebar bg-obsidian-950 border-r border-line flex flex-col z-30" data-testid="app-sidebar">
      <div className="h-topbar flex items-center gap-3 px-5 border-b border-line">
        <div className="w-8 h-8 rounded-sm bg-citron grid place-items-center font-mono text-sm font-bold text-obsidian-950">GR</div>
        <div className="leading-tight">
          <div className="font-heading text-sm font-bold tracking-tight">GROUPE ROPS</div>
          <div className="overline text-[9px]">v2 · Direction</div>
        </div>
      </div>

      <div ref={entityRef} className="p-4 border-b border-line relative">
        <div className="overline mb-2">Entité</div>
        <button
          onClick={() => setEntityOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 px-3 h-11 bg-obsidian-900 hover:bg-obsidian-850 border border-line rounded-sm transition-colors"
          data-testid="entity-switcher-button"
        >
          <span className="flex items-center gap-2.5 min-w-0">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: currentEntity?.color || "#D6F32F" }} />
            <span className="text-left min-w-0">
              <div className="text-sm font-semibold truncate">{currentEntity?.name}</div>
              <div className="text-[10px] text-obsidian-400 truncate">{currentEntity?.sub}</div>
            </span>
          </span>
          <ChevronsUpDown size={14} className="text-obsidian-400 flex-shrink-0" />
        </button>
        <AnimatePresence>
          {entityOpen && (
            <motion.div
              initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.12 }}
              className="absolute left-4 right-4 top-[calc(100%-4px)] z-40 bg-obsidian-900 border border-line rounded-sm shadow-2xl max-h-[60vh] overflow-y-auto"
              data-testid="entity-dropdown"
            >
              {entities.map((e) => (
                <button
                  key={e.id}
                  onClick={() => { selectEntity(e.id); setEntityOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 h-11 hover:bg-obsidian-800 border-b border-line/50 last:border-b-0 transition-colors text-left"
                  data-testid={`entity-option-${e.id}`}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: e.color }} />
                  <span className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{e.name}</div>
                    <div className="text-[10px] text-obsidian-400 truncate">{e.sub}</div>
                  </span>
                  {currentEntity?.id === e.id && <Check size={14} className="text-citron flex-shrink-0" />}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-3">
        {SECTIONS.filter(canSeeSection).map((section, si) => (
          <div key={si}>
            <div className="overline px-2 mb-1.5">{section.label}</div>
            {section.items.filter(canSeeItem).map((item) => <NavItem key={item.to} {...item} />)}
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-line">
        <button
          onClick={onOpenAI}
          className="w-full flex items-center gap-2.5 px-3 h-10 bg-gradient-to-r from-citron/10 to-transparent border border-citron/30 hover:border-citron/60 hover:bg-citron/15 rounded-sm transition-all"
          data-testid="ai-panel-button"
        >
          <Sparkles size={14} className="text-citron animate-pulse-glow rounded-full" />
          <span className="text-sm font-medium text-citron">Assistant IA</span>
          <kbd className="ml-auto text-[10px] font-mono px-1.5 py-0.5 bg-obsidian-900 rounded text-obsidian-400">⌘K</kbd>
        </button>
      </div>

      <div className="p-3 border-t border-line flex items-center gap-3">
        <div className="w-8 h-8 grid place-items-center bg-obsidian-800 rounded-sm font-mono text-xs font-bold text-citron">
          {user?.full_name?.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate">{user?.full_name}</div>
          <div className="text-[10px] text-obsidian-400 truncate">{user?.role}</div>
        </div>
        <button
          onClick={() => { logout(); navigate("/login"); }}
          className="btn-ghost h-8 w-8 p-0"
          title="Déconnexion"
          data-testid="logout-button"
        >
          <LogOut size={14} />
        </button>
      </div>
    </aside>
  );
}

function NavItem({ to, label, icon: Icon, badge }) {
  return (
    <NavLink
      to={to}
      data-testid={`nav-${to.split("/").pop()}`}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-2 h-8 rounded-sm text-[13px] transition-colors ${
          isActive
            ? "bg-citron/10 text-citron border-l-2 border-citron pl-3"
            : "text-obsidian-300 hover:text-obsidian-50 hover:bg-obsidian-900"
        }`
      }
    >
      <Icon size={14} />
      <span className="truncate flex-1">{label}</span>
      {badge && <span className="text-[8px] font-mono px-1 py-0.5 bg-citron/20 text-citron rounded">{badge}</span>}
    </NavLink>
  );
}
